import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCards";
import AdSlot from "@/components/AdSlot";
import { Share2, Bookmark, ArrowLeft } from "lucide-react";

export default function ArticlePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, behavior: "auto" });
    api.get(`/articles/${slug}`).then(r => setA(r.data)).catch(() => setA(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-slate-500" data-testid="article-loading">Loading…</div>;
  if (!a) return <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="article-not-found">
    <h1 className="font-editorial text-3xl font-bold mb-2">Article not found</h1>
    <Link to="/" className="text-sky-700">Back to homepage →</Link>
  </div>;

  const share = () => {
    if (navigator.share) navigator.share({ title: a.title, url: window.location.href });
    else navigator.clipboard.writeText(window.location.href);
  };

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="article-page">
      <Link to="/" className="text-xs text-slate-500 flex items-center gap-1 mb-6 hover:text-sky-700"><ArrowLeft className="w-3 h-3"/>Back</Link>

      <div className="flex flex-wrap gap-2 mb-4">
        {a.categories?.map(c => <Link key={c} to={`/category/${c}`} className="overline text-[10px] px-3 py-1 rounded-full tag-blue">{c.replace(/-/g," ")}</Link>)}
        {a.is_breaking && <span className="overline text-[10px] px-3 py-1 rounded-full bg-red-500 text-white">Breaking</span>}
      </div>

      <h1 className="headline text-3xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="article-title">{a.title}</h1>
      {a.subtitle && <p className="text-xl text-slate-600 font-light leading-snug mb-6">{a.subtitle}</p>}

      <div className="flex flex-wrap items-center justify-between border-y border-slate-100 py-4 mb-8 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-700 grid place-items-center font-bold">{(a.author_name || "E")[0]}</div>
          <div>
            <div className="text-sm font-semibold text-slate-900">{a.author_name}</div>
            <div className="text-xs text-slate-500">{formatDate(a.published_at)}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={share} className="p-2.5 rounded-full bg-slate-100 hover:bg-sky-100 text-slate-600 hover:text-sky-700" data-testid="article-share-btn"><Share2 className="w-4 h-4"/></button>
          <button className="p-2.5 rounded-full bg-slate-100 hover:bg-sky-100 text-slate-600 hover:text-sky-700" data-testid="article-bookmark-btn"><Bookmark className="w-4 h-4"/></button>
        </div>
      </div>

      {a.featured_image && (
        <div className="rounded-3xl overflow-hidden mb-8 bg-slate-100"><img src={a.featured_image} alt={a.title} className="w-full max-h-[60vh] object-cover"/></div>
      )}

      <div className="prose-editorial text-slate-800" dangerouslySetInnerHTML={{ __html: (a.content || "").replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ") }} data-testid="article-content" />

      {/* Inline mid-article ad */}
      <AdSlot placement="article-inline" className="my-8 max-w-2xl mx-auto"/>

      {(a.source_url || a.source_name) && (
        <div className="mt-8 p-4 rounded-2xl bg-sky-50/60 border border-sky-100 text-sm text-slate-700" data-testid="article-source">
          Source: {a.source_url ? (
            <a href={a.source_url} target="_blank" rel="noreferrer" className="text-sky-700 font-semibold">{a.source_name || a.institution || a.source_url}</a>
          ) : (
            <span className="text-sky-700 font-semibold">{a.source_name || a.institution}</span>
          )}. AI-assisted news write-up by EduTime Today.
        </div>
      )}

      {a.tags?.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2">
          {a.tags.map(t => <Link key={t} to={`/search?q=${t}`} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-sky-100">#{t}</Link>)}
        </div>
      )}

      {a.related?.length > 0 && (
        <section className="mt-14" data-testid="related-articles">
          <h2 className="font-editorial text-2xl font-bold mb-6">Continue reading</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {a.related.map(r => <ArticleCard key={r.slug} article={r} />)}
          </div>
        </section>
      )}
    </article>
  );
}
